/**
 * Rôles et niveaux d'accès (devis DEV-2026-10-RI, module « Accès et traçabilité »).
 *
 * - `admin`          : administration technique (Umbeli, puis le responsable
 *                      désigné) — comptes, réglages, journal complet.
 * - `direction`      : voit tout le métier, journal d'audit, attribution des
 *                      dossiers aux avocats.
 * - `recrutement`    : crée et fait avancer les dossiers candidats, dépose et
 *                      valide les pièces, lance les imports.
 * - `administration` : lecture des dossiers, suivi de la liste de contrôle des
 *                      pièces, notifications ; ne supprime rien.
 * - `avocat`         : accès restreint aux SEULS dossiers qui lui sont confiés ;
 *                      récupère et dépose des documents.
 */
export const ROLES = ["admin", "direction", "recrutement", "administration", "avocat"] as const;
export type UserRole = (typeof ROLES)[number];

export const ROLE_LABELS: Record<UserRole, string> = {
  admin: "Administrateur",
  direction: "Direction",
  recrutement: "Recrutement",
  administration: "Administration",
  avocat: "Avocat",
};

/** Rôles internes : tout sauf les avocats (partenaires externes). */
export const STAFF_ROLES: readonly UserRole[] = ["admin", "direction", "recrutement", "administration"];

/** Rôles qui peuvent créer et modifier un dossier candidat. */
export const EDITOR_ROLES: readonly UserRole[] = ["admin", "direction", "recrutement"];

/** Rôles qui lisent le journal d'audit et attribuent les dossiers aux avocats. */
export const SUPERVISOR_ROLES: readonly UserRole[] = ["admin", "direction"];

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  createdAt: number | string;
  /** Double authentification activée sur ce compte. */
  mfaEnabled: boolean;
  /** Le compte doit configurer la double authentification avant d'aller plus loin. */
  mfaSetupRequired?: boolean;
  active?: boolean;
  lastLoginAt?: number | string | null;
}

/** Réglages d'instance modifiables depuis l'écran Réglages (clé/valeur). */
export interface AppSettings {
  organisationName: string;
  timezone: string;
  contactEmail: string;
}

/** Une ligne du journal de consultation et de modification. */
export interface AuditEvent {
  id: string;
  at: number | string;
  userId: string | null;
  userEmail: string | null;
  role: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  ip: string | null;
  details: Record<string, unknown> | null;
}
